import BaseNodeModel from "./BaseNodeModel";
import DesignerSetting from "../DesignerSetting";
import Utility from './../utilities/Utility';
import LocalVariablesUsedIn from"../utilities/usedIn/LocalVariableUsedIn";
import VariablesUsedIn from"../utilities/usedIn/VariablesUsedIn";
import SensitivePropsDisallowed from "../utilities/SensitivePropsDisallowed";
import { updateLastChange } from "../utilities";
export default class VariableDataModel extends BaseNodeModel {
    /**
     * Variable data model; no visible node views should be available
     * @module VariableDataModel
     * @param {Object} props Variable properties
     * @param {string} id If non-null, use this as node ID
     */
    constructor(props = null, id = null, config = {}) {
        super("Variable", 0, 0, null, id);
        this.props.nodeTypeName = "Variable Definition";
        this.props.colorClass = "pink";
        this.props.iconClass = "hash";
        this.props.usedIn = [];
        this.props.config = {};
        this.props.config.id = Utility.uuid4();
        this.props.config["sensitiveData"] = false;
        this.props.config["type"] = VariableDataModel.getType("STRING");
        this.props.config["defaultValue"] = "";
        
        this.DEFAULT_COLOR_CLASS = "pink";
        this.type = "variable";
        // omnichannel enabled set default scope on config
        if(window.apm.serialized.omniChannel.isOmniChannel){
                this.props.config["scope"] = "session";
        }
        if (props && props.hasOwnProperty("varName")) {
            // Schema only
            this.parseSchema(props);
        } else {
            // Full node JSON
            this.applyProps(props);
        }
        this.isDialog = config.isDialog || false;
    }

    /**
     * Does variable partially matches the search term?
     * @param {string} query
     * @param {Object} data Serialized variable data model
     * @returns {boolean}
     */
    static match(query, data) {
        if (!data["varName"]) {
            return false;
        }
        if (!query) {
            return true;
        }
        const regex = new RegExp(query, "i");
        return data["varName"].match(regex);
    }

    /**
     * Is the variable of primitive type?
     * @param {string} type Variable type
     * @returns {boolean}
     */
    static isPrimitive(type) {
        return type === "STRING" || type === "NUMBER" || type === "BOOLEAN";
    }

    /**
     * Get qualified variable type name
     * @param {string} type
     * @returns {string}
     */
    static getType(type) {
        const idx = window.apm.knowledgeBase.getVariableTypeIndexFromValue(type);
        if (type && idx >= 0) {
            return type;
        }
        return "STRING";
    }

    /**
     * Get the display name for a variable type
     * @param {string} type
     * @returns {string}
     */
    static getTypeName(type) {
        const idx = window.apm.knowledgeBase.getVariableTypeIndexFromValue(type);
        if (type && idx >= 0) {
            return window.apm.knowledgeBase.getVariableTypes()[idx].name;
        }
        return "";
    }
   
    setConfig(key, value) {
        if (this.props.config[key] !== value) {
            if (!this.props.createdTime) {
                this.props.createdTime = window.apm.timestamp;
            }
            updateLastChange(this.props);
        }
        if (key === "varName") {
            // this field is unused?
            this.props.config.name = value;
            const parentModule = this.props.parentModule || null;
            const varModel = window.apm.getVariable(this.id, this.props.parentModule)  || false;
            if(varModel){
                Utility.openApplicationOverlay('Updating variable name references...');
                const oldName = varModel.props.config.varName;
                const usedIn = VariablesUsedIn.getVariableUsedIn(varModel, window.apm.serialized);
                setTimeout(() => {
                    window.apm.refactorVariable(varModel, parentModule, { varName: value }, usedIn, oldName);
                }, 100);
            }      
        }
        if (key === "type") {
            // Refresh property panel when changing variable type
            this.props.config.type = value;
            if (value === "BOOLEAN") {
                this.props.config.defaultValue = false;
            }
            window.apm.postEventToCurrentView({ target: "request-variable-property-panel", 
                event: { node: this } })
        }
        if (key === "defaultValue" && this.props.config.defaultValue !== value) {
           if(this.props.parentModule){
            window.apm.updateDefaultValue(this,this.props.config.defaultValue, value,this.props.parentModule);
           } 
            if (this.props.config.type === "BOOLEAN" && (value === "true" || value === "false")) {
                if (value === "true") {
                    this.props.config[key] = true;
                    value = true;
                }
                if (value === "false") {
                    this.props.config[key] = false;
                    value = false;
                }
            }
            if (this.props.config.type === "NUMBER") {
                this.props.config[key] = Number(value);
                value = Number(value);
            }
        }
        
        if (key === "sensitiveData" && this.props.config[key] !== value && !this.props.parentModule) {
            this.props.config[key] = value;
            window.apm.updateSensitiveData(this.id, this.props.config.varName, value);
        }
        
        if (this.props.parentModule && key === "moduleInput") {
            var moduleIndex = window.apm.serialized.components.modules.findIndex(mo => mo.id === this.props.parentModule);
            var varIndex = window.apm.serialized.components.modules[moduleIndex].localVariables.findIndex(lv => lv.id === this.id);
            if (varIndex !== -1) {
                if (value === true) {
                    this.props.config.moduleInput = value;
                    window.apm.addModuleInput(this,this.props.parentModule,window.apm.serialized);
                }
                else {
                    window.apm.deleteModuleInput(this,this.props.parentModule,window.apm.serialized);
                }
            }
           
        }
        
        super.setConfig(key, value);

        const simultaneousEnabled = window.apm.serialized.omniChannel.isOmniChannel;
        if(simultaneousEnabled){
            if(key === "scope"){
                window.apm.refreshApplicationTree();
            }
        }

        if(this.isDialog){ // update dialog (e.g., NewLocalVariableDialog)
            document.dispatchEvent(new CustomEvent("ev-dialog-variable-changed", {detail: {variable: this}}));
        } else { // update canvas
            document.dispatchEvent(new CustomEvent("request-refresh", {detail: {component: this}}));
            document.dispatchEvent(new CustomEvent("ev-property-refresh"));
                
        }
        window.apm.setSerializedChanged();

    }
    /**
     * Suppress components' updateContext
     * UI that provides multiple variable properties may want to defer updateContext to an 'Add' button 
     * See NewLocalVariableDialog for example
     */
    getShouldSuppressUpdate(){
        // when creating a lv in a dialog 'Add' button in dialog handles updateContext for whole variable 
        return this.isDialog;
    }

    getConfigViews() {
        const suppressUpdate = this.getShouldSuppressUpdate();
        // TODO: https://jira.interactions.net/browse/CORE-29543: Sensitive Variable loophole
        let sensitiveDataDisabled = false;
        // Dev Drew note: A previous commit had a for loop that breaks the first time a true value is found
        // forEach loops can't be broken, so even if a true value is found, it keeps running.
        // hotfix - new variable dialog uses embedded property panel. cannot send uncreated variables to usedin
        if(!this.isEmbed) {
            SensitivePropsDisallowed.forEach(key => {
                if(this.checkVarUsedInSensitive(key)){
                    sensitiveDataDisabled = true;
                }
            });
        }

        var nameField = { 
                type: "Input", 
                label: this.getPropLabel("variable_name"), 
                key: "varName", 
                valRules: { "variableName": true, "noEmpty": true, "noDuplicateVar": true },
                suppressUpdate
            };
        var defaultValue = { 
            type: "Input", 
            label: this.getPropLabel("default_value"), 
            key: "defaultValue", 
            tooltip:"Default value for this variable",
            suppressUpdate
        };
        var moduleInput = {};
        var booleanType = {};
        
        if (this.getConfig("type") === "NUMBER") {
            defaultValue.inputRules = { numberOnly: true };
        }
        if (this.getConfig("type") === "PROMPT") {
            defaultValue.suggestion = 'prompt';
        }
        if (this.getConfig("type") === "MODULE") {
            defaultValue.suggestion = 'module';
        }
        if (this.getConfig("type") === "BOOLEAN") {
            booleanType = { 
                type: "Switch", 
                key: "defaultValue", 
                label: this.getPropLabel("default_value"), 
                checked: false,
                tooltip: 'Default value for this variable',
                suppressUpdate
            };
            defaultValue = {};
        }
        if (this.props.parentModule) {
            moduleInput = { 
                type: "Switch", 
                key: "moduleInput", 
                label: this.getPropLabel("module_input"), 
                checked: false,
                tooltip:"Module input variables",
                suppressUpdate
            };
        }

        const simultaneousEnabled = window.apm.serialized.omniChannel.isOmniChannel;

        let scopeField = {};
        // show the scope dropdown when omnichannel is marked as true in settings page
        if(simultaneousEnabled && !this.props.parentModule){
            scopeField = {
                type: "Dropdown", 
                key: "scope", 
                label: this.getPropLabel("scope"), 
                optionName: "Select scope",
                options: [
                    {name: "Session", value: "session"},
                    {name: "Conversation", value: "conversation"}
                ]
            };
        }

        const configViews =  [
            nameField,
            {   type: "Input", 
                label: this.getPropLabel("description"), 
                key: "description",
                suppressUpdate
             },
             
            
            {
                type: "Dropdown", 
                key: "type", 
                label: this.getPropLabel("type"), 
                optionName: "Select type",
                options: window.apm.knowledgeBase.getVariableTypes(), 
                readOnly: this.props.parentModule !== undefined && window.apm.getCurrentDiagramWidget().props.node.props.isCommonModule,
                suppressUpdate
            },
            
            scopeField,

            defaultValue,
            booleanType,
            moduleInput,
            {   type: "Switch", 
                key: "sensitiveData", 
                label: this.getPropLabel("sensitive_data"), 
                checked: false,
                suppressUpdate,
                disabled: sensitiveDataDisabled,
                callback: sensitiveDataDisabled ? () => this.usedInWarning() : null
            },
            {   type: "Switch", 
                key: "constant", 
                label: this.getPropLabel("read_only"),
                suppressUpdate 
            }, 
            {   type: "Textarea", 
                label: this.getPropLabel("note"), 
                key: "note",
                suppressUpdate 
            },
            {   type: "hr" }
           
        ];  

        if(!simultaneousEnabled){
            delete configViews.scopeField;
        }

        return configViews

    }

    getConfig(key) {
        if (key === "name") {
            return this.props.config["varName"] + (this.props.config["alias"] ? " (" + this.props.config["alias"] + ")" : "");
        }
        if (key === "usedIn") {
            return this.getProperty(key);
        }
        return this.props.config[key];
    }

    /**
     * Apply serialized model to instance model
     * @param schema
     */
    parseSchema(schema) {
        this.props.config["alias"] = schema.alias;
        this.props.config["varName"] = schema.varName;
        this.props.config["type"] = VariableDataModel.getType(schema.type);
        this.props.config["value"] = schema.value;
        this.props.config["defaultValue"] = schema.defaultValue;
        this.props.config["constant"] = schema.constant;
    }

    serialize() {
        const s = super.serialize();
        delete s.x;
        delete s.y;
        return s;
    }

    /**
     * Checks to see if the variable is used in Destination input on Session Node Model
     * @param {Object} viewConfig View configuration
     * @returns {XML}
     */
     checkVarUsedInSensitive(key) {
        let disableSensitive = false
        //If the variable is a local variable, we use the parent node to check where it's used in for sensitive data exclusion
        if(this.props.parentModule) {
            const getParentModule = window.apm.serialized.components.modules.find(mod => mod.id === this.props.parentModule);
            const getLocalVariable = getParentModule.localVariables.find(localVar => localVar.id === this.id);
            // TODO: hot-fix to filter out global intent items from localVar usedIn.
            const usedIn = LocalVariablesUsedIn.getVariableUsedIn(getLocalVariable, getParentModule, window.apm.serialized).filter(item => item.type !== "inGlobalIntent"
                                                                                                                                        && item.type !== "inAppProperties"
                                                                                                                                        && item.type !== "inReusableFunction");
            usedIn.forEach(node => {
                // LocalVariableUsedIn response interface is inconsistent:
                let useObj = null;
                
                if(node.type === "inFlowNode"){
                    useObj = node.node;
                }
                if(node.type === "inFlowLink"){
                    useObj = node.link;
                }
                if(node.type === "inApplicationObjectConfig"){
                    useObj = node.obj;
                }
                // Some keys refer to an array collection of properties to check rather than a single property
                if(Array.isArray(useObj.props.config[key])){
                    disableSensitive = useObj.props.config[key].some(property => property.varName === "mod."+getLocalVariable.name);

                } else if(useObj.props.config[key] && useObj.props.config[key] === "mod."+getLocalVariable.name){
                    disableSensitive = true;
                }
            });
        } else {
        //If the variable is an app variable, we check used in for sensitive data exclusion
            const getAppVariable = window.apm.serialized.components.variables.find(appVar => appVar.id === this.id);
            const usedIn = VariablesUsedIn.getVariableUsedIn(getAppVariable, window.apm.serialized).filter(item => item.type !== "inGlobalIntent" 
                                                                                                                && item.type !== "inAppProperties"
                                                                                                                && item.type !== "inReusableFunction");
            usedIn.forEach(node => {
                // LocalVariableUsedIn response interface is inconsistent:
                let useObj = null;
                
                if(node.type === "inFlowNode"){
                    useObj = node.node;
                }
                if(node.type === "inFlowLink"){
                    useObj = node.link;
                }
                if(node.type === "inApplicationObjectConfig"){
                    useObj = node.obj;
                }
                // Some keys refer to an array collection of properties to check rather than a single property
                if(Array.isArray(useObj.props.config[key])){
                    disableSensitive = useObj.props.config[key].some(property => property.varName === "app."+getAppVariable.name);
                    
                } else if(useObj.props.config[key] && useObj.props.config[key] === "app."+getAppVariable.name){
                    disableSensitive = true;
                }
            });
        }
        return disableSensitive
    }


    usedInWarning() {
        window.apm.notification.post("error", "Property that variable is used in prohibits sensitive data", true);
    }
}